import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, styled } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import { isNotNullOrUndefined, normalizeFileName } from "../../../../../../common/utils/utils";

import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import PrimaryButton from "../../../../../../common/components/primary_button";
import { ClienteArquivoForm } from "../../../schemas/Clientes_cadastro/clienteArquivoCadastro";
import { NovosNegociosArquivoForm, NovosNegociosArquivoSchema } from "../../../schemas/Novos_negocios_cadastro/novosArquivoCadastro";
import { NovosNegociosArquivo } from "../../../../domain/models/novos_negocios_arquivo";

/**
 * VisuallyHiddenInput
 */

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

/**
 * CadastroDocumentoModal
 */

type Props = {
    open: boolean,
    saving: boolean,
    selectedNovosNegociosArquivo: NovosNegociosArquivo | null,

    onSubmit: (form: NovosNegociosArquivoForm) => void,
    onOpenChangeHistory: (id: number, tabelaTipo: LogTipoTabela) => void,
    onDownload: (idNovosNegociosArquivo: number, imageName: string) => void,
    onClose: () => void,
};

export const NovosNegociosArquivoModal = ({
    open,
    saving,
    selectedNovosNegociosArquivo,

    onSubmit,
    onOpenChangeHistory,
    onDownload,
    onClose,
}: Props) => {
    const [novosNegociosArquivo, setNovosNegociosArquivo] = useState(selectedNovosNegociosArquivo);

    const hookForm = useForm<NovosNegociosArquivoForm>({
        mode: "onSubmit",
        reValidateMode: "onSubmit",
        resolver: zodResolver(NovosNegociosArquivoSchema),
    });

    useEffect(() => {
        if (open) {
            if (selectedNovosNegociosArquivo) {

                hookForm.setValue('idNovosNegociosArquivo', selectedNovosNegociosArquivo.idNovosNegociosArquivo);
                hookForm.setValue('descricao', selectedNovosNegociosArquivo.descricao);
                hookForm.setValue('nome', selectedNovosNegociosArquivo.nome);
                hookForm.setValue('arquivo', null);

                hookForm.setValue('limparArquivo', false);
            } else {
                hookForm.reset();
            }
            setNovosNegociosArquivo(selectedNovosNegociosArquivo ?? null);
        }
    }, [
        open,
        selectedNovosNegociosArquivo,
        hookForm,
    ]);

    const handleClose = useCallback(() => {
        if (!saving) onClose();
    }, [
        saving,
        onClose,
    ]);

    const handleDownload = useCallback(() => {
        if (!saving && novosNegociosArquivo)
            onDownload(novosNegociosArquivo.idNovosNegociosArquivo ?? 0, novosNegociosArquivo.nome ?? '');
    }, [
        saving,
        novosNegociosArquivo,
        onDownload,
    ]);

    const handleDeleteFile = useCallback(() => {
        if (!saving) {
            if (window.confirm('Você deseja excluir este item?')) {
                hookForm.setValue('limparArquivo', true);
                setNovosNegociosArquivo(null);
            }
        }
    }, [
        saving,
        hookForm,
    ]);

    const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>, onChange: (e: any) => void) => {
        if (!saving && e.target.files && e.target.files.length > 0) {
            hookForm.setValue('limparArquivo', false);
            onChange(e.target.files.item(0));
        }
    }, [
        saving,
        hookForm,
    ]);

    const handleSubmit = useCallback((form: ClienteArquivoForm) => {
        if (!saving) onSubmit(form);
    }, [
        saving,
        onSubmit,
    ]);

    const handleChangeHistory = useCallback(() => {
        if (selectedNovosNegociosArquivo) {
            onOpenChangeHistory(selectedNovosNegociosArquivo.idNovosNegociosArquivo ?? 0, LogTipoTabela.NOVOS_NEGOCIOS_ARQUIVO);
        }
    }, [selectedNovosNegociosArquivo, onOpenChangeHistory]);

    return (
        <Dialog
            open={open}
            slotProps={{
                paper: {
                    component: 'form',
                    onSubmit: hookForm.handleSubmit(handleSubmit),
                }
            }}
            sx={{
                "& .MuiDialog-container": {
                    "& .MuiPaper-root": {
                        width: "100%",
                        maxWidth: "45rem",
                    },
                },
            }}>
            <DialogTitle>
                Cadastrar arquivo
            </DialogTitle>

            <DialogContent>
                <ModalLoading loading={saving}>
                    <FormContainer title={"Cadastro Arquivo"} arrangement="fullRow">
                        <>

                            <DefaultFormInput
                                name={'descricao'}
                                label={"Nome do Arquivo"}
                                readOnly={saving}
                                required={true}
                                reactHookForm={hookForm}
                            />

                            <div className="flex w-full">
                                <Controller
                                    name='arquivo'
                                    control={hookForm.control}
                                    render={({ field }) => (novosNegociosArquivo && novosNegociosArquivo.nome && novosNegociosArquivo.nome.length > 0) ? (
                                        <div className="w-full flex flex-row justify-between items-center">
                                            <span
                                                className="cursor-pointer"
                                                onMouseDown={handleDownload}>
                                                {novosNegociosArquivo.nome && novosNegociosArquivo.nome.length > 40
                                                    ? `${novosNegociosArquivo.nome.substring(0, 40)}...`
                                                    : novosNegociosArquivo.nome}
                                            </span>
                                            <IconButton onClick={handleDeleteFile}>
                                                <DynamicIcon nameIcon="DeleteForeverRounded" size={25} className="text-red-500" />
                                            </IconButton>
                                        </div>
                                    ) : (
                                        <PrimaryButton
                                            component="label"
                                            role={undefined}
                                            variant="contained"
                                            tabIndex={-1}
                                            disabled={saving}
                                            startIcon={<DynamicIcon nameIcon="Download" size={25} className="text-white" />}>
                                            {field.value ? normalizeFileName(field.value.name, 40) : "Escolher arquivo"}
                                            <VisuallyHiddenInput
                                                type="file"
                                                onChange={(e) => handleFileChange(e, field.onChange)}
                                                multiple={false} />
                                        </PrimaryButton>
                                    )} />
                            </div>
                        </>
                    </FormContainer>
                </ModalLoading>
            </DialogContent>

            <DialogActions>
                <div className="w-full h-full items-center justify-between flex p-4 gap-4">
                    <div>
                        {
                            isNotNullOrUndefined(novosNegociosArquivo) &&
                            <PrimaryButton
                                buttonColor="yellow"
                                onClick={handleChangeHistory}>
                                <span className="text-xs">
                                    Historico de alterações
                                </span>
                            </PrimaryButton>
                        }
                    </div>

                    <div className="flex flex-row gap-2">
                        <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
                            <h1
                                className="text-sm text-black dark:text-white"
                                onClick={handleClose}>
                                Cancelar
                            </h1>
                        </div>

                        <div className="w-[120px]">
                            {saving ? (
                                <PrimaryButton
                                    buttonColor="primary-green">
                                    Salvando...
                                </PrimaryButton>
                            ) : (
                                <PrimaryButton
                                    type="submit"
                                    buttonColor="primary-green">
                                    Salvar
                                </PrimaryButton>
                            )}
                        </div>
                    </div>
                </div>
            </DialogActions>
        </Dialog>
    );
}